import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppDatePicker } from '../components/AppDatePicker';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  ExpenseClaimDto,
  ExpenseClaimLineItemDto,
  ExpenseClaimDocumentDto,
  ExpenseCategory,
  CreateExpenseClaimLineItemDto,
  User,
} from '../types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { DocumentUploadDialog } from '../components/DocumentUploadDialog';
import { SupportingDocumentsTable } from '../components/SupportingDocumentsTable';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import { formatDate, formatCalendarDateIso, parseUtcDate } from '../utils/dateFormat';
import { useExpenseVatOptions } from '../hooks/useExpenseVatOptions';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotSave, couldNotSubmit, couldNotRevise, documentCopy } from '../utils/userFacingCopy';

interface LineItemRow extends CreateExpenseClaimLineItemDto {
  id: string;
  lineItemId?: string;
}

function toLineItemRows(items: ExpenseClaimLineItemDto[]): LineItemRow[] {
  return items.map((item, index) => ({
    id: item.id,
    lineItemId: item.id,
    description: item.description,
    expenseCategoryId: item.expenseCategoryId,
    amount: item.amount,
    sortOrder: item.sortOrder ?? index,
  }));
}

function toCreateLineItems(rows: LineItemRow[]): CreateExpenseClaimLineItemDto[] {
  return rows.map((r, i) => ({
    description: r.description,
    expenseCategoryId: r.expenseCategoryId,
    amount: r.amount,
    sortOrder: i,
  }));
}

export default function NewExpenseClaimPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { user } = useAuth();

  const [claimId, setClaimId] = useState<string | null>(editId);
  const [claim, setClaim] = useState<ExpenseClaimDto | null>(null);
  const [loading, setLoading] = useState(!!editId);
  const [error, setError] = useState<string>('');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);

  const [claimDate, setClaimDate] = useState<Date | null>(() => new Date());
  const [payee, setPayee] = useState('');
  const [vatRate, setVatRate] = useState(15); // default South Africa
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([
    { id: '1', description: '', expenseCategoryId: '', amount: 0, sortOrder: 0 },
  ]);
  const [onBehalfOfEmployeeId, setOnBehalfOfEmployeeId] = useState<string>('');

  const [documents, setDocuments] = useState<ExpenseClaimDocumentDto[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewNotAvailableFileName, setPreviewNotAvailableFileName] = useState<string | null>(null);
  const [saveSubmitting, setSaveSubmitting] = useState(false);
  const [submitSubmitting, setSubmitSubmitting] = useState(false);
  const [reviseSubmitting, setReviseSubmitting] = useState(false);

  const { options: vatOptions, getCurrencySymbol } = useExpenseVatOptions();
  const canCreateOnBehalf = user?.roles?.includes('ExpenseClaimOnBehalf') || user?.roles?.includes('Administrator');
  const isDraft = claim?.status === 'Draft';
  const canEditClaim = claim != null && (claim.requesterId === user?.id || claim.createdByUserId === user?.id);
  const isReadOnly = claim != null && (!isDraft || !canEditClaim);

  const loadClaim = useCallback(async (id: string) => {
    try {
      const res = await apiService.get<ExpenseClaimDto>(API_ENDPOINTS.EXPENSE_CLAIMS.BY_ID(id));
      const c = res.data;
      if (c) {
        setClaim(c);
        setClaimDate(parseUtcDate(c.claimDate));
        setPayee(c.payee);
        setVatRate(c.vatRate);
        setNotes(c.notes ?? '');
        setLineItems(
          c.lineItems?.length
            ? toLineItemRows(c.lineItems)
            : [{ id: '1', description: '', expenseCategoryId: '', amount: 0, sortOrder: 0 }]
        );
      }
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('the expense claim')));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocuments = useCallback(async (id: string) => {
    setDocumentsLoading(true);
    try {
      const res = await apiService.get<ExpenseClaimDocumentDto[]>(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENTS(id));
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  useEffect(() => {
    apiService.get<ExpenseCategory[]>(API_ENDPOINTS.REFERENCE_DATA.EXPENSE_CATEGORIES.BASE).then((r) => {
      setCategories(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (canCreateOnBehalf) {
      apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE).then((r) => {
        setEmployees(Array.isArray(r.data) ? r.data : []);
      }).catch(() => setEmployees([]));
    }
  }, [canCreateOnBehalf]);

  useEffect(() => {
    if (editId) {
      setClaimId(editId);
      loadClaim(editId);
    }
  }, [editId, loadClaim]);

  useEffect(() => {
    if (claimId) loadDocuments(claimId);
  }, [claimId, loadDocuments]);

  // Default VAT to first option from API when creating a new claim
  useEffect(() => {
    if (vatOptions.length > 0 && !claimId) setVatRate(vatOptions[0].vatRate);
  }, [vatOptions, claimId]);

  const subtotal = lineItems.reduce((s, r) => s + (Number.isFinite(r.amount) ? r.amount : 0), 0);
  const vatAmount = (subtotal * vatRate) / 100;
  const total = subtotal + vatAmount;
  const currencySymbol = getCurrencySymbol(vatRate);
  const vatOptionLabel = vatOptions.find((o) => o.vatRate === vatRate)?.label ?? `${vatRate}%`;

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { id: crypto.randomUUID(), description: '', expenseCategoryId: categories[0]?.id ?? '', amount: 0, sortOrder: prev.length },
    ]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length <= 1) return;
    setLineItems((prev) => prev.filter((r) => r.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItemRow, value: string | number) => {
    setLineItems((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const handleSaveDraft = async () => {
    const validItems = lineItems.filter((r) => r.description.trim() && r.expenseCategoryId && (r.amount > 0));
    if (validItems.length === 0) {
      setError('Add at least one line item with description, category and amount.');
      return;
    }
    setSaveSubmitting(true);
    setError('');
    try {
      const payload = {
        claimDate: claimDate ? formatCalendarDateIso(claimDate) + 'T00:00:00Z' : formatCalendarDateIso(new Date()) + 'T00:00:00Z',
        payee: payee.trim(),
        poNumber: null,
        vatRate,
        notes: notes.trim() || null,
        lineItems: toCreateLineItems(validItems),
        ...(claimId ? {} : { onBehalfOfEmployeeId: onBehalfOfEmployeeId || null }),
      };
      if (claimId) {
        await apiService.put(API_ENDPOINTS.EXPENSE_CLAIMS.BY_ID(claimId), payload);
        await loadClaim(claimId);
      } else {
        const res = await apiService.post<ExpenseClaimDto>(API_ENDPOINTS.EXPENSE_CLAIMS.BASE, payload);
        const created = res.data;
        if (created?.id) {
          setClaimId(created.id);
          setClaim(created);
          await loadClaim(created.id);
        }
      }
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotSave('your draft')));
    } finally {
      setSaveSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!claimId) {
      setError('Save the draft first, then submit.');
      return;
    }
    const validItems = lineItems.filter((r) => r.description.trim() && r.expenseCategoryId && (r.amount > 0));
    if (validItems.length === 0) {
      setError('Add at least one line item before submitting.');
      return;
    }
    setSubmitSubmitting(true);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.EXPENSE_CLAIMS.SUBMIT(claimId));
      setClaim((c) => (c ? { ...c, status: 'PendingManagerApproval' } : null));
      navigate('/expenses/me');
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotSubmit('the claim')));
    } finally {
      setSubmitSubmitting(false);
    }
  };

  const handleUploadReceipts = async (files: File[], _description: string) => {
    if (!claimId || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await apiService.post(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENTS_UPLOAD(claimId), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await loadDocuments(claimId);
      setUploadDialogOpen(false);
    } catch (err) {
            setError(getApiErrorMessage(err, documentCopy.uploadReceipt));
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = async (documentId: string, fileName: string, contentType: string) => {
    if (!claimId) return;
    try {
      setError('');
      const res = await apiService.get<Blob>(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENT_DOWNLOAD(claimId, documentId), {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        setPreviewNotAvailableFileName(fileName);
      }
    } catch {
      setError(documentCopy.open);
    }
  };

  const handleDownloadDocument = async (documentId: string, fileName: string) => {
    if (!claimId) return;
    try {
      const res = await apiService.get<Blob>(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENT_DOWNLOAD(claimId, documentId), {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(documentCopy.download);
    }
  };

  const handleDeleteDocument = async (documentId: string, _fileName: string) => {
    if (!claimId) return;
    try {
      await apiService.delete(API_ENDPOINTS.EXPENSE_CLAIMS.DOCUMENT_DELETE(claimId, documentId));
      await loadDocuments(claimId);
    } catch {
      setError(documentCopy.delete);
    }
  };

  const handleRevise = async () => {
    if (!claimId) return;
    setReviseSubmitting(true);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.EXPENSE_CLAIMS.REVISE(claimId));
      await loadClaim(claimId);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotRevise('the claim')));
    } finally {
      setReviseSubmitting(false);
    }
  };

  if (loading && editId) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} size="small" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight="bold">
            {claimId ? (isReadOnly ? 'Expense claim' : 'Edit expense claim') : 'New expense claim'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {claim?.status === 'Rejected' && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleRevise}
                disabled={reviseSubmitting}
                sx={{ fontWeight: 600, textTransform: 'none', px: 3, py: 1 }}
              >
                {reviseSubmitting ? '…' : 'Revise claim'}
              </Button>
            }
          >
            <Typography variant="body1" fontWeight="600" gutterBottom>
              This claim was rejected.
            </Typography>
            <Typography variant="body2">
              {claim.rejectedReason ? (
                <>
                  <strong>Reason:</strong> {claim.rejectedReason}
                  <br />
                  <br />
                </>
              ) : null}
              Use &quot;Revise claim&quot; to return it to draft, add receipts or make changes, then submit again.
            </Typography>
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Claim details
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppDatePicker
                  label="Claim date"
                  value={claimDate}
                  onChange={(d) => setClaimDate(d)}
                  disabled={isReadOnly}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 160 },
                    },
                  }}
                />
              </LocalizationProvider>
              <TextField
                label="Payee"
                value={payee}
                onChange={(e) => setPayee(e.target.value)}
                disabled={isReadOnly}
                size="small"
                required
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 100 }} disabled={isReadOnly}>
                <InputLabel>VAT %</InputLabel>
                <Select value={vatRate} label="VAT %" onChange={(e) => setVatRate(Number(e.target.value))}>
                  {vatOptions.map((opt) => (
                    <MenuItem key={opt.vatRate} value={opt.vatRate}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {!claimId && canCreateOnBehalf && (
              <FormControl size="small" sx={{ minWidth: 260, mt: 2 }} fullWidth>
                <InputLabel>Submit on behalf of (optional)</InputLabel>
                <Select
                  value={onBehalfOfEmployeeId}
                  label="Submit on behalf of (optional)"
                  onChange={(e) => setOnBehalfOfEmployeeId(e.target.value)}
                >
                  <MenuItem value="">Myself</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isReadOnly}
              size="small"
              multiline
              rows={2}
              fullWidth
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Line items
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell align="right"><strong>Amount ({currencySymbol})</strong></TableCell>
                    {!isReadOnly && <TableCell width={56} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.description}
                          onChange={(e) => updateLineItem(row.id, 'description', e.target.value)}
                          disabled={isReadOnly}
                          placeholder="Description"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth disabled={isReadOnly}>
                          <Select
                            value={row.expenseCategoryId}
                            displayEmpty
                            onChange={(e) => updateLineItem(row.id, 'expenseCategoryId', e.target.value)}
                          >
                            <MenuItem value="">Select category</MenuItem>
                            {categories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>{cat.glCode} – {cat.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          value={row.amount || ''}
                          onChange={(e) => updateLineItem(row.id, 'amount', parseFloat(e.target.value) || 0)}
                          disabled={isReadOnly}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <IconButton size="small" onClick={() => removeLineItem(row.id)} disabled={lineItems.length <= 1} aria-label="Remove row">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!isReadOnly && (
              <Button startIcon={<AddIcon />} onClick={addLineItem} size="small" sx={{ mt: 1 }}>
                Add line item
              </Button>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Summary
            </Typography>
            <Typography>Subtotal: {currencySymbol}{subtotal.toFixed(2)}</Typography>
            <Typography>VAT ({vatOptionLabel}): {currencySymbol}{vatAmount.toFixed(2)}</Typography>
            <Typography fontWeight="600">Total: {currencySymbol}{total.toFixed(2)}</Typography>
          </CardContent>
        </Card>

        {claimId && (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="600">
                  Receipts
                </Typography>
                {!isReadOnly && isDraft && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Upload Document
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isReadOnly
                  ? 'Receipts and supporting documents attached to this claim. You can view or download them.'
                  : 'Attach receipts or supporting documents for this claim. You can view, download, or remove them while the claim is in draft.'}
              </Typography>
              {documentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {isReadOnly ? 'No documents attached to this claim.' : 'No documents yet. Use "Upload Document" to add receipts or supporting files.'}
                </Typography>
              ) : (
                <SupportingDocumentsTable
                  documents={documents.map((d) => ({
                    id: d.id,
                    fileName: d.fileName,
                    description: undefined,
                    uploadedByName: d.uploadedByName,
                    uploadedAt: d.uploadedAt,
                    fileSize: d.fileSize,
                    contentType: d.contentType,
                  }))}
                  onView={handleViewDocument}
                  onDownload={handleDownloadDocument}
                  onDelete={handleDeleteDocument}
                  canEdit={!isReadOnly}
                  formatDate={(dateStr) => formatDate(dateStr, { includeTime: true })}
                />
              )}
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!isReadOnly && (
            <>
              <Button variant="contained" onClick={handleSaveDraft} disabled={saveSubmitting}>
                {saveSubmitting ? <CircularProgress size={20} /> : 'Save draft'}
              </Button>
              {claimId && (
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={submitSubmitting}>
                  {submitSubmitting ? <CircularProgress size={20} /> : 'Submit for approval'}
                </Button>
              )}
            </>
          )}
          {!claimId && (
            <Button variant="outlined" onClick={() => navigate('/expenses/me')}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      <DocumentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUploadReceipts}
        uploading={uploading}
        title="Upload Document"
        allowMultiple={true}
      />

      <Dialog open={!!previewNotAvailableFileName} onClose={() => setPreviewNotAvailableFileName(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview not available</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This file type cannot be displayed in the browser. Please use Download to open it on your device.
          </Typography>
          {previewNotAvailableFileName && (
            <Typography variant="body2" sx={{ mt: 1 }} fontWeight={500}>
              File: {previewNotAvailableFileName}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewNotAvailableFileName(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
